// client/src/components/NoAccessScreen.jsx
// Shown when a user is authenticated via Google BUT hasn't been
// provisioned in the back office (not a super admin, no managedCamps).
// Per Eliahou: don't expose the empty-camp dashboard to them — instead
// route back to a "login-style" screen that explains the situation and
// offers the signup form / a logout to try a different Google account.
//
// Reached from App.jsx whenever
// `!user.isSuperAdmin && (!user.managedCamps || user.managedCamps.length === 0)`.

import { useNavigate } from 'react-router-dom';
import Logo from './Logo.jsx';
import VersionFooter from './VersionFooter.jsx';
import { useLogout } from '../hooks/useMe.js';

export default function NoAccessScreen({ user }) {
  const logout = useLogout();
  // The user reaches this screen ONLY when authenticated — so the
  // signup CTA should navigate straight to /apply, not bounce them
  // through another /auth/google round-trip (which just re-authorises
  // the same Google account and feels broken to the user).
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-start justify-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-md animate-fade-in">
          <div className="card-pop p-8 sm:p-10 text-center relative overflow-hidden">
            {/* Joy-tinted strap to signal "this isn't the standard
                in-and-running state" without being alarming. */}
            <div className="absolute inset-x-0 top-0 h-2 bg-joy-gradient" />
            <Logo className="mx-auto block w-full max-w-[260px] h-auto" />

            <div className="mt-4 rounded-2xl bg-joy-50 border border-joy-200 px-4 py-3 text-start">
              <div className="text-sm font-bold text-joy-900">
                לא נמצא חשבון פעיל עבורך
              </div>
              <div className="mt-1 text-sm text-joy-800">
                התחברת עם{' '}
                <span dir="ltr" className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">
                  {user.email}
                </span>
                , אבל המייל הזה עוד לא הוקצה לאף תכנית במערכת.
              </div>
            </div>

            <p className="mt-5 text-sm text-ink-700">
              בית הספר שלך מעוניין להפעיל TicTac? מלא טופס קצר ונחזור אליך
              תוך יום-יומיים.
            </p>

            <button
              type="button"
              className="btn-pop btn-lg w-full mt-4 justify-center"
              onClick={() => navigate('/apply')}
            >
              פתח חשבון לבית הספר
            </button>

            <button
              type="button"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="btn-secondary w-full mt-2 justify-center"
            >
              {logout.isPending ? 'מתנתק...' : 'התנתק והשתמש בחשבון אחר'}
            </button>

            <p className="mt-4 text-xs text-ink-500">
              כבר מנהל ומחפש את התכנית שלך? בדוק שאתה משתמש באותו אימייל
              שעבר אישור. אם יש עדיין בעיה — כתבו לנו ב־{' '}
              <a
                href="mailto:office@nedar.im"
                dir="ltr"
                className="font-semibold text-brand-700 hover:text-brand-900 underline"
              >
                office@nedar.im
              </a>
              .
            </p>
          </div>
        </div>
      </main>
      <VersionFooter />
    </div>
  );
}
